import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { AIThinkingState } from "@/components/AltioraComponents";
import { toast } from "sonner";
import {
  BookOpen, FileText, Handshake, TrendingUp, Scale, GraduationCap,
  Download, Search, ChevronRight, Sparkles, Quote, X, Clock, Edit3, History, PenTool,
} from "lucide-react";

const COLLECTION_ICON = {
  founder_manuals: BookOpen,
  white_papers: FileText,
  partner_kits: Handshake,
  commercial: TrendingUp,
  legal: Scale,
  training: GraduationCap,
};

export default function Library() {
  useI18n();
  const [taxonomy, setTaxonomy] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState(null);           // opened document
  const [current, setCurrent] = useState({ coll: null, sub: null });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCite, setShowCite] = useState(false);

  const loadDocs = async (coll = null, sub = null, query = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (coll) params.set("collection_key", coll);
      if (sub) params.set("subcollection_key", sub);
      if (query) params.set("q", query);
      const r = await api.get(`/library/documents?${params.toString()}&limit=100`);
      setDocuments(r.data.items || []);
    } catch { toast.error("Chargement bibliothèque impossible"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const t = await api.get("/library/taxonomy");
        setTaxonomy(t.data.collections || []);
        await loadDocs();
      } catch { toast.error("Chargement bibliothèque impossible"); }
    })();
  }, []);

  const openDoc = async (id) => {
    try {
      const r = await api.get(`/library/documents/${id}`);
      setSelected(r.data);
    } catch { toast.error("Document introuvable"); }
  };

  const downloadPdf = async (id, title) => {
    try {
      const resp = await api.get(`/library/documents/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch { toast.error("Échec téléchargement PDF"); }
  };

  const filterBy = (coll, sub = null) => {
    setCurrent({ coll, sub });
    setQ("");
    loadDocs(coll, sub);
  };

  const filtered = useMemo(() => documents, [documents]);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="library-root">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-5 w-5 text-navy" strokeWidth={2} />
              <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy">Founder Library</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
              Bibliothèque ALTIORA
            </h1>
            <p className="text-base md:text-lg text-[#374151] mt-5 max-w-2xl leading-relaxed">
              Documentation stratégique de référence. Consultation en ligne, téléchargement PDF, citation IA.
              Chaque document est versionné et attribuable.
            </p>
          </div>
          <button
            onClick={() => setShowCite(true)}
            data-testid="library-cite-open"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium bg-navy text-white hover:bg-navy-700 transition shadow-sm"
          >
            <Quote className="h-4 w-4" strokeWidth={2} /> Sur quoi vous basez-vous ?
          </button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar taxonomy */}
          <aside className="col-span-12 md:col-span-3">
            <div className="bg-white border border-[#E4E1D8] rounded-lg p-3 sticky top-4" data-testid="library-taxonomy">
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-4 w-4 text-[#5B5B62]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadDocs(current.coll, current.sub, q); }}
                  placeholder="Rechercher…"
                  className="flex-1 px-2 py-1 text-sm bg-[#F1EFE9] rounded focus:outline-none"
                  data-testid="library-search-input"
                />
              </div>
              <button
                onClick={() => { setCurrent({ coll: null, sub: null }); loadDocs(); }}
                className={`w-full text-left px-2 py-1.5 text-sm rounded ${!current.coll ? "bg-[#0B2545] text-white" : "text-[#1F2937] hover:bg-[#F1EFE9]"}`}
              >
                Tout ({taxonomy.reduce((a, c) => a + c.total_count, 0)})
              </button>
              <div className="mt-2 space-y-1">
                {taxonomy.map((c) => {
                  const Icon = COLLECTION_ICON[c.key] || BookOpen;
                  const isActive = current.coll === c.key;
                  return (
                    <div key={c.key}>
                      <button
                        onClick={() => filterBy(c.key)}
                        className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded ${isActive ? "bg-[#F1EFE9] text-[#0B2545]" : "text-[#1F2937] hover:bg-[#F1EFE9]"}`}
                        data-testid={`taxonomy-${c.key}`}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <Icon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
                          <span className="truncate">{c.label_fr}</span>
                        </span>
                        <span className="text-[10px] text-[#8B8B8F]">{c.total_count}</span>
                      </button>
                      {isActive && c.subcollections?.length > 0 && (
                        <div className="ml-5 mt-1 space-y-0.5">
                          {c.subcollections.map((sc) => (
                            <button
                              key={sc.key}
                              onClick={() => filterBy(c.key, sc.key)}
                              className={`w-full flex items-center justify-between px-2 py-1 text-[12px] rounded ${current.sub === sc.key ? "bg-[#0B2545] text-white" : "text-[#5B5B62] hover:bg-[#F1EFE9] hover:text-[#1F2937]"}`}
                            >
                              <span className="truncate">{sc.label_fr}</span>
                              <span className="text-[10px] opacity-70">{sc.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Document list */}
          <main className="col-span-12 md:col-span-9">
            {loading ? (
              <AIThinkingState label="Chargement des documents…" />
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-[#5B5B62] bg-white border border-[#E4E1D8] rounded-lg">
                Aucun document. Essayez une autre catégorie ou recherche.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="library-doc-list">
                {filtered.map((d) => {
                  const Icon = COLLECTION_ICON[d.collection_key] || BookOpen;
                  const coll = taxonomy.find((c) => c.key === d.collection_key);
                  const sub = coll?.subcollections.find((s) => s.key === d.subcollection_key);
                  return (
                    <button
                      key={d.id}
                      onClick={() => openDoc(d.id)}
                      className="text-left bg-white border border-[#E4E1D8] rounded-lg p-4 hover:border-[#0B2545] hover:shadow-md transition"
                      data-testid="library-doc-card"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-md bg-[#F1EFE9] flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8B8B8F]">
                            {coll?.label_fr}{sub ? ` · ${sub.label_fr}` : ""}
                          </div>
                          <h3 className="text-sm font-display font-medium text-[#1F2937] tracking-tight leading-snug mt-0.5">
                            {d.title_fr}
                          </h3>
                          {d.summary_fr && (
                            <p className="text-xs text-[#5B5B62] mt-1.5 line-clamp-2">{d.summary_fr}</p>
                          )}
                          <div className="mt-2 flex items-center gap-3 text-[10px] text-[#8B8B8F]">
                            <span>v{d.version}</span>
                            <span>·</span>
                            <span>{d.views || 0} vues</span>
                            {d.cited_count > 0 && <><span>·</span><span>{d.cited_count} citations IA</span></>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-[#8B8B8F] mt-2" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </main>
        </div>

        {selected && <DocumentReader doc={selected} taxonomy={taxonomy} onClose={() => setSelected(null)} onDownload={() => downloadPdf(selected.id, selected.title_fr)} />}
        {showCite && <CiteModal onClose={() => setShowCite(false)} onOpen={openDoc} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document reader
// ---------------------------------------------------------------------------
function DocumentReader({ doc, taxonomy, onClose, onDownload }) {
  const { user } = useAuth();
  const isFounder = (user?.roles || []).some((r) => ["founder", "admin", "super_admin"].includes(r));
  const isLegal = doc.collection_key === "legal";
  const [showEditor, setShowEditor] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [showTranslate, setShowTranslate] = useState(false);
  // Reading analytics — track time on doc
  const startedAt = useRef(Date.now());
  useEffect(() => {
    return () => {
      const elapsed = Date.now() - startedAt.current;
      if (elapsed >= 1500) {
        api.post(`/library/documents/${doc.id}/progress`, {
          completion_pct: 100, reading_time_ms: Math.min(elapsed, 3_600_000),
        }).catch(() => {});
      }
    };
  }, [doc.id]);
  const coll = taxonomy.find((c) => c.key === doc.collection_key);
  const sub = coll?.subcollections.find((s) => s.key === doc.subcollection_key);
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="library-reader">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full md:w-[720px] bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between z-10 gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">
              {coll?.label_fr}{sub ? ` · ${sub.label_fr}` : ""} · v{doc.version}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {isFounder && (
              <>
                <button onClick={() => setShowEditor(true)} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#F1EFE9] text-[#0B2545] rounded hover:bg-[#E4E1D8]" data-testid="library-edit-btn">
                  <Edit3 className="h-3 w-3" /> Éditer
                </button>
                <button onClick={() => setShowVersions(true)} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#F1EFE9] text-[#0B2545] rounded hover:bg-[#E4E1D8]" data-testid="library-versions-btn">
                  <History className="h-3 w-3" /> Versions
                </button>
                <button onClick={() => setShowTranslate(true)} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#F1EFE9] text-[#7A5B14] rounded hover:bg-[#E4E1D8]">
                  <Sparkles className="h-3 w-3" /> Traduire
                </button>
              </>
            )}
            {isLegal && (
              <button onClick={() => setShowSign(true)} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white rounded" data-testid="library-sign-btn">
                <PenTool className="h-3 w-3" /> Signer
              </button>
            )}
            <button
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0B2545] text-white rounded-md hover:bg-[#123163]"
              data-testid="library-download-pdf"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </button>
            <button onClick={onClose} className="p-1 text-[#5B5B62] hover:text-[#1F2937]" data-testid="library-reader-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="px-6 py-6">
          <h1 className="text-2xl font-display font-medium text-[#0B2545] tracking-tight mb-2">{doc.title_fr}</h1>
          {doc.summary_fr && (
            <p className="text-sm italic text-[#5B5B62] mb-6 pb-4 border-b border-[#E4E1D8]">{doc.summary_fr}</p>
          )}
          <MarkdownView markdown={doc.content_md} />
          <div className="mt-8 pt-4 border-t border-[#E4E1D8] flex items-center justify-between text-[11px] text-[#8B8B8F]">
            <span>Mis à jour : {new Date(doc.updated_at).toLocaleDateString("fr-CA")}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> v{doc.version} · {doc.views} vues · {doc.downloads || 0} téléchargements
            </span>
          </div>
          {isLegal && (doc.signatures?.length > 0) && (
            <div className="mt-4 p-3 bg-[#FBF6EA] rounded border border-[#C5A880]/40">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A5B14] mb-2">Signatures ({doc.signatures.length})</div>
              {doc.signatures.map((s) => (
                <div key={s.id} className="text-xs text-[#1F2937] py-1 border-b border-[#E4E1D8] last:border-0">
                  <div className="font-medium">{s.party_name} {s.party_role ? `· ${s.party_role}` : ""}</div>
                  <div className="text-[10px] text-[#8B8B8F]">
                    Signé le {new Date(s.signed_at).toLocaleDateString("fr-CA")} · v{s.document_version} · hash {s.hash?.slice(0, 10)}…
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showEditor && <EditorModal doc={doc} onClose={() => setShowEditor(false)} onSaved={() => { setShowEditor(false); onClose(); }} />}
      {showVersions && <VersionsModal doc={doc} onClose={() => setShowVersions(false)} />}
      {showSign && <SignModal doc={doc} onClose={() => setShowSign(false)} onSigned={() => { setShowSign(false); onClose(); }} />}
      {showTranslate && <TranslateModal doc={doc} onClose={() => setShowTranslate(false)} onDone={() => { setShowTranslate(false); onClose(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor modal — markdown + preview
// ---------------------------------------------------------------------------
function EditorModal({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({
    collection_key: doc.collection_key, subcollection_key: doc.subcollection_key,
    title_fr: doc.title_fr, summary_fr: doc.summary_fr || "",
    content_md: doc.content_md || "", version: doc.version || "1.0.0",
    tags: (doc.tags || []).join(", "), audience: (doc.audience || []).join(", "),
    lang: doc.lang || "fr", published: doc.published !== false,
  });
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (form.title_fr.length < 4 || form.content_md.length < 20) {
      toast.error("Titre >=4 et contenu >=20 requis"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        audience: form.audience.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/library/documents", payload);
      toast.success(`Document mis à jour (v${form.version})`);
      onSaved();
    } catch { toast.error("Échec de la sauvegarde"); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="library-editor-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Éditer : {doc.title_fr}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview((p) => !p)} className="px-2.5 py-1 text-xs bg-[#F1EFE9] text-[#0B2545] rounded">
              {preview ? "Édition" : "Aperçu"}
            </button>
            <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <input className={inputCls} placeholder="Titre" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} data-testid="editor-title" />
            <input className={inputCls} placeholder="Version (1.0.0)" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            <select className={inputCls} value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}>
              <option value="fr">FR</option><option value="en">EN</option><option value="ar">AR</option>
            </select>
          </div>
          <input className={inputCls} placeholder="Résumé (max 500 car.)" value={form.summary_fr} onChange={(e) => setForm({ ...form, summary_fr: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Tags (séparés par virgules)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            <input className={inputCls} placeholder="Audience (all, employers…)" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} />
          </div>
          {preview ? (
            <div className="border border-[#E4E1D8] rounded-md p-4 min-h-[400px] bg-[#FBFBF9]">
              <MarkdownView markdown={form.content_md} />
            </div>
          ) : (
            <textarea
              className={inputCls + " font-mono text-xs leading-relaxed min-h-[400px]"}
              value={form.content_md}
              onChange={(e) => setForm({ ...form, content_md: e.target.value })}
              data-testid="editor-content"
              placeholder="# Titre en markdown..."
            />
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-between">
          <label className="text-xs text-[#5B5B62] inline-flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Publié
          </label>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
            <button onClick={save} disabled={busy} className="px-4 py-1.5 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163] disabled:opacity-50" data-testid="editor-save">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Versions modal
// ---------------------------------------------------------------------------
function VersionsModal({ doc, onClose }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/library/documents/${doc.id}/versions`);
        setHistory(r.data);
      } catch { toast.error("Échec"); }
    })();
  }, [doc.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="library-versions-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Historique des versions</h3>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4">
          {!history ? <div className="text-sm text-[#8B8B8F]">Chargement…</div> : (
            <div className="space-y-2">
              <div className="p-3 bg-[#E0E7FF] rounded border border-[#0B2545]/20">
                <div className="text-[10px] font-mono uppercase tracking-wide text-[#0B2545]">Version actuelle</div>
                <div className="text-sm font-medium">v{history.current_version} — {history.current_title}</div>
              </div>
              {(history.history || []).length === 0 && (
                <div className="text-xs text-[#8B8B8F] italic">Aucune version antérieure (seulement version originale).</div>
              )}
              {(history.history || []).slice().reverse().map((v, i) => (
                <div key={i} className="p-3 border border-[#E4E1D8] rounded">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">v{v.version} — {v.title_fr}</div>
                    <div className="text-[10px] text-[#8B8B8F]">{new Date(v.updated_at).toLocaleDateString("fr-CA")}</div>
                  </div>
                  {v.summary_fr && <div className="text-xs text-[#5B5B62] mt-1 italic">{v.summary_fr}</div>}
                  <div className="text-[10px] text-[#8B8B8F] mt-1">Par {v.updated_by}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sign modal (legal docs)
// ---------------------------------------------------------------------------
function SignModal({ doc, onClose, onSigned }) {
  const [form, setForm] = useState({ party_name: "", party_role: "", consent_text: "Signé électroniquement via ALTIORA" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (form.party_name.length < 2) { toast.error("Nom requis"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/library/documents/${doc.id}/sign`, form);
      toast.success(`Signature enregistrée · hash ${r.data.signature.hash.slice(0, 12)}…`);
      onSigned();
    } catch { toast.error("Échec de la signature"); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="library-sign-modal">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Signature électronique</h3>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="text-xs text-[#5B5B62] italic">
            Document : <strong>{doc.title_fr}</strong> · v{doc.version}
          </div>
          <input className={inputCls} placeholder="Nom de la partie (ex : Alpha Incubator)" value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} data-testid="sign-party-name" />
          <input className={inputCls} placeholder="Rôle (ex : Directeur général)" value={form.party_role} onChange={(e) => setForm({ ...form, party_role: e.target.value })} />
          <textarea className={inputCls + " min-h-[70px] text-xs"} value={form.consent_text} onChange={(e) => setForm({ ...form, consent_text: e.target.value })} />
          <div className="text-[10px] text-[#8B8B8F]">
            La signature génère un hash SHA-256 immuable du document + partie + horodatage. Preuve juridique conservée à vie.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white rounded-md disabled:opacity-50" data-testid="sign-submit">
            {busy ? "Signature…" : "Signer maintenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Translate modal
// ---------------------------------------------------------------------------
function TranslateModal({ doc, onClose, onDone }) {
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);
  const translate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/library/documents/${doc.id}/translate`, { target_lang: lang });
      toast.success(`Traduction ${lang.toUpperCase()} créée`);
      onDone(r.data.id);
    } catch { toast.error("Traduction indisponible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-display text-[#1F2937]">Traduire ce document</h3>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="text-xs text-[#5B5B62]">
            Traduction via LLM (Claude Sonnet 4.6). Le nouveau document est ajouté à la Library avec la langue cible.
          </div>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md">
            <option value="en">English</option>
            <option value="ar">العربية (Arabic)</option>
            <option value="es">Español</option>
            <option value="pt">Português</option>
            <option value="de">Deutsch</option>
          </select>
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
          <button onClick={translate} disabled={busy} className="px-4 py-1.5 text-sm bg-[#0B2545] text-white rounded-md disabled:opacity-50">
            {busy ? "Traduction (LLM ~30s)…" : "Traduire"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minimal markdown renderer (safe, no external deps)
// ---------------------------------------------------------------------------
function MarkdownView({ markdown }) {
  const blocks = renderMarkdown(markdown || "");
  return <div className="prose-altiora">{blocks}</div>;
}

function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let key = 0;
  const kget = () => `md-${key++}`;
  while (i < lines.length) {
    const line = lines[i];
    // Fenced code
    if (line.startsWith("```")) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      out.push(
        <pre key={kget()} className="bg-[#F1EFE9] rounded-md p-3 text-xs font-mono text-[#1F2937] overflow-x-auto my-3">
          <code>{codeLines.join("\n")}</code>
        </pre>,
      );
      continue;
    }
    if (line.startsWith("# ")) { out.push(<h1 key={kget()} className="text-2xl font-display text-[#0B2545] mt-6 mb-3">{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith("## ")) { out.push(<h2 key={kget()} className="text-lg font-display text-[#0B2545] mt-5 mb-2">{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith("### ")) { out.push(<h3 key={kget()} className="text-sm font-medium text-[#1F2937] mt-4 mb-1.5">{line.slice(4)}</h3>); i++; continue; }
    if (line.startsWith("> ")) { out.push(<blockquote key={kget()} className="border-l-2 border-[#C5A880] pl-3 my-3 italic text-[#7A5B14] text-sm">{renderInline(line.slice(2))}</blockquote>); i++; continue; }
    if (line.match(/^\s*[-*] /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*[-*] /)) {
        items.push(<li key={kget()} className="mb-1">{renderInline(lines[i].replace(/^\s*[-*] /, ""))}</li>);
        i++;
      }
      out.push(<ul key={kget()} className="list-disc list-inside text-sm text-[#1F2937] space-y-0.5 my-3 pl-2">{items}</ul>);
      continue;
    }
    if (line.match(/^\s*\d+\. /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*\d+\. /)) {
        items.push(<li key={kget()} className="mb-1">{renderInline(lines[i].replace(/^\s*\d+\.\s*/, ""))}</li>);
        i++;
      }
      out.push(<ol key={kget()} className="list-decimal list-inside text-sm text-[#1F2937] space-y-0.5 my-3 pl-2">{items}</ol>);
      continue;
    }
    if (line === "---") { out.push(<hr key={kget()} className="my-4 border-[#E4E1D8]" />); i++; continue; }
    if (line.trim() === "") { out.push(<div key={kget()} className="h-2" />); i++; continue; }
    out.push(<p key={kget()} className="text-sm text-[#1F2937] leading-relaxed my-2">{renderInline(line)}</p>);
    i++;
  }
  return out;
}

function renderInline(text) {
  // Very light inline: **bold**, *italic*, `code`
  const parts = [];
  let key = 0;
  const kget = () => `inl-${key++}`;
  let remaining = text;
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/;
  while (remaining) {
    const m = remaining.match(regex);
    if (!m) { parts.push(<span key={kget()}>{remaining}</span>); break; }
    const idx = m.index;
    if (idx > 0) parts.push(<span key={kget()}>{remaining.slice(0, idx)}</span>);
    const token = m[0];
    if (token.startsWith("**")) parts.push(<strong key={kget()} className="text-[#0B2545]">{token.slice(2, -2)}</strong>);
    else if (token.startsWith("*")) parts.push(<em key={kget()}>{token.slice(1, -1)}</em>);
    else if (token.startsWith("`")) parts.push(<code key={kget()} className="bg-[#F1EFE9] px-1 rounded text-xs">{token.slice(1, -1)}</code>);
    remaining = remaining.slice(idx + token.length);
  }
  return <>{parts}</>;
}

// ---------------------------------------------------------------------------
// Cite modal — « Sur quoi vous basez-vous ? »
// ---------------------------------------------------------------------------
function CiteModal({ onClose, onOpen }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const run = async () => {
    if (q.trim().length < 3) { toast.error("3 caractères minimum"); return; }
    setBusy(true);
    try {
      const r = await api.post("/library/cite", { query: q.trim(), max_results: 5 });
      setResults(r.data);
    } catch { toast.error("Échec"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="library-cite-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">Founder Library · Citation IA</div>
            <h3 className="text-lg font-display text-[#1F2937]">Sur quoi vous basez-vous ?</h3>
          </div>
          <button onClick={onClose} className="p-1 text-[#5B5B62]" data-testid="library-cite-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5">
          <div className="flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) run(); }}
              placeholder="Ex : Comment ALTIORA garantit-il la confidentialité des données ?"
              className="flex-1 px-3 py-2 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]"
              data-testid="library-cite-input"
            />
            <button
              onClick={run}
              disabled={busy}
              className="px-4 py-2 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163] disabled:opacity-50"
              data-testid="library-cite-submit"
            >
              {busy ? "Recherche…" : "Citer"}
            </button>
          </div>
          <div className="text-[10px] text-[#8B8B8F] mt-2">
            Retrieval déterministe sur toute la Founder Library. Les Manuels fondateurs sont prioritaires.
          </div>

          {results && (
            <div className="mt-5 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">
                {results.citations.length} document(s) cité(s) · mots-clés : {results.keywords.join(", ")}
              </div>
              {results.citations.length === 0 && (
                <div className="p-3 bg-[#F1EFE9] rounded text-sm text-[#5B5B62]">
                  Aucun document pertinent trouvé. Reformulez ou ajoutez un document manquant.
                </div>
              )}
              {results.citations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { onOpen(c.id); onClose(); }}
                  className="w-full text-left p-3 border border-[#E4E1D8] rounded-md hover:border-[#0B2545] transition"
                  data-testid="library-cite-result"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">
                        {c.collection_label_fr} · {c.subcollection_label_fr} · v{c.version}
                      </div>
                      <div className="text-sm font-medium text-[#1F2937] mt-0.5">{c.title_fr}</div>
                      {c.summary_fr && <div className="text-xs text-[#5B5B62] mt-1 line-clamp-2">{c.summary_fr}</div>}
                    </div>
                    <div className="text-[10px] font-mono text-[#7A5B14]">score {c.score}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
